import React, { useState } from "react";
import { Eye, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";

/**
 * Lets an admin look at the product as somebody else's tier.
 *
 * The switch is not cosmetic — the server downgrades the principal for every
 * subsequent request, so a paywalled endpoint returns the same 402 a real free
 * member gets. That is the point: the only way to trust what a free member sees is
 * to be one.
 *
 * Shown only to real admins, and the bar stays visible while previewing because it
 * is the way back out.
 */
const MODES = [
  { key: null, labelKey: "adminPreview.asAdmin" },
  { key: "investor", labelKey: "adminPreview.asInvestor" },
  { key: "paid", labelKey: "adminPreview.asPaid" },
  { key: "free", labelKey: "adminPreview.asFree" },
];

export default function AdminPreviewBar() {
  const { t } = useTranslation();
  const { isRealAdmin, adminPreview } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!isRealAdmin) return null;

  const choose = async (mode) => {
    if (mode === adminPreview || busy) return;
    setBusy(true);
    try {
      await api.setAdminPreview(mode);
      // A full reload is deliberate: role and tier change what nearly every cached
      // query is allowed to return, so re-fetching piecemeal would leave the screen
      // showing a mix of two identities.
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("dialogs.failed"));
      setBusy(false);
    }
  };

  const previewing = !!adminPreview;
  return (
    <div
      data-testid="admin-preview-bar"
      className={`flex items-center gap-2 rounded-xl px-2.5 py-2 text-xs ${
        previewing ? "bg-amber-50 ring-1 ring-amber-300" : "bg-slate-50"
      }`}
    >
      {busy ? (
        <Loader2 size={13} className="animate-spin text-slate-400 shrink-0" />
      ) : (
        <Eye size={13} className={`shrink-0 ${previewing ? "text-amber-600" : "text-slate-400"}`} />
      )}
      <div className="flex flex-wrap gap-1">
        {MODES.map((m) => (
          <button
            key={m.key || "admin"}
            type="button"
            data-testid={`preview-${m.key || "admin"}`}
            onClick={() => choose(m.key)}
            className={`rounded-lg px-2 py-1 font-medium transition-colors ${
              (adminPreview || null) === m.key
                ? "bg-white text-brand-700 shadow-sm ring-1 ring-slate-200"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            {t(m.labelKey)}
          </button>
        ))}
      </div>
    </div>
  );
}
